// src/components/OnlineStatusDropdown.tsx
import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { turso } from '@/database/client';

type OnlineStatus = 'online' | 'away' | 'busy' | 'offline';

interface StatusOption {
  value: OnlineStatus;
  label: string;
  icon: string;
  color: string;
}

const STATUS_OPTIONS: StatusOption[] = [
  { value: 'online', label: 'Online (Available)', icon: '🟢', color: 'bg-emerald-500/20 border-emerald-400/40 text-emerald-300' },
  { value: 'away', label: 'Away (Stepped away)', icon: '🟡', color: 'bg-amber-500/20 border-amber-400/40 text-amber-300' },
  { value: 'busy', label: 'Busy (Do not disturb)', icon: '🔴', color: 'bg-red-500/20 border-red-400/40 text-red-300' },
  { value: 'offline', label: 'Offline (Appear offline)', icon: '⚫', color: 'bg-slate-500/20 border-slate-400/40 text-slate-300' },
];

export function OnlineStatusDropdown() {
  const { user, refreshUser } = useAuth();
  const [isUpdating, setIsUpdating] = useState(false);
  const [localStatus, setLocalStatus] = useState<OnlineStatus | null>(null);
  
  // Get current status from user data (default to 'online' if show_online_status is true)
  const currentStatus: OnlineStatus = localStatus || user?.online_status || (user?.show_online_status ? 'online' : 'offline');

  const updateStatus = async (newStatus: OnlineStatus) => {
    if (!user) return;
    
    console.log('[OnlineStatus] Button clicked:', newStatus);
    
    if (isUpdating) {
      console.log('[OnlineStatus] Already updating, skipping');
      return;
    }
    
    setIsUpdating(true);
    setLocalStatus(newStatus); // Immediate UI update
    
    try {
      // Check if using localStorage fallback
      const users = localStorage.getItem('whipt_dev_users');
      console.log('[OnlineStatus] localStorage users:', users ? 'found' : 'not found');
      
      if (users) {
        const userList = JSON.parse(users);
        const userIndex = userList.findIndex((u: any) => u.id === user.id);
        if (userIndex !== -1) {
          userList[userIndex].online_status = newStatus;
          userList[userIndex].show_online_status = newStatus !== 'offline';
          localStorage.setItem('whipt_dev_users', JSON.stringify(userList));
          console.log('[OnlineStatus] Updated localStorage:', newStatus);
        } else {
          console.log('[OnlineStatus] User not found in localStorage');
        }
      } else {
        // Try to update in database (column may not exist yet)
        console.log('[OnlineStatus] Attempting database update...');
        try {
          await turso.execute({
            sql: 'UPDATE users SET online_status = ?, show_online_status = ? WHERE id = ?',
            args: [newStatus, newStatus !== 'offline' ? 1 : 0, user.id]
          });
          console.log('[OnlineStatus] ✅ Database updated successfully');
        } catch (dbError: any) {
          if (dbError.message?.includes('no such column')) {
            console.log('[OnlineStatus] ⚠️  online_status column not yet added to database, using local state only');
            // Column doesn't exist yet - just use local state
          } else {
            throw dbError; // Re-throw other errors
          }
        }
      }
      
      await refreshUser();
      console.log('[OnlineStatus] User refreshed successfully');
    } catch (error) {
      console.error('[OnlineStatus] Failed to update:', error);
      // Keep local state change even if refresh fails
    } finally {
      setIsUpdating(false);
      console.log('[OnlineStatus] Update complete');
    }
  };

  if (!user) return null;

  return (
    <div className="flex items-center gap-2">
      {STATUS_OPTIONS.map((option) => {
        const isActive = option.value === currentStatus;
        return (
          <button
            key={option.value}
            onClick={() => {
              if (!isUpdating) updateStatus(option.value);
            }}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border-2 transition-all ${
              isActive 
                ? option.color + ' font-semibold'
                : 'bg-black/20 border-white/10 text-slate-400 hover:bg-black/30 hover:border-white/20'
            } cursor-pointer hover:scale-105`}
            title={option.label}
          >
            <span className="text-sm">{option.icon}</span>
          </button>
        );
      })}
    </div>
  );
}
